import { useCallback, useState } from "react"

import { FormDialog } from "components/dialog/form"

import { EvaluationFormResult } from "interface/evaluationForm"
import { EvaluationSectionRow, usePostEvaluationResult } from "interface/search/evaluation"
import { FormControl, MenuItem, Select, Stack, TextField } from "@mui/material"

type useSectionRowEditFormProps = {
  searchId: number
  kojinCode: string
  fields: {
    contentFieldId: string,
    scoreFieldId: string
  },
  storeData: {
    content: string | undefined,
    score: string | undefined
  },
  scores: EvaluationFormResult["scores"]
  onSubmit: (row: EvaluationSectionRow) => void
}
const useSectionRowEditForm = ({
  searchId,
  kojinCode,
  fields: {
    contentFieldId,
    scoreFieldId
  },
  storeData,
  scores,
  onSubmit
}: useSectionRowEditFormProps) => {
  const [content, setContent] = useState<string>(storeData.content ?? '')
  const [score, setScore] = useState<string>(storeData.score ?? '')

  const onPosted = useCallback(
    () => {
      const value = scores.find(s => s.value === score)?.score ?? 0
      onSubmit({
        contentFieldId,
        content,
        scoreFieldId,
        score,
        value
      })
    },
    [onSubmit, contentFieldId, content, scoreFieldId, score, scores]
  )
  const [postState, post] = usePostEvaluationResult(
    searchId,
    kojinCode,
    { onPosted }
  )
  const submit = useCallback(
    () => {
      post({
        fields: [
          { fieldId: contentFieldId, value: content.trim() },
          { fieldId: scoreFieldId, value: score.trim() }
        ]
      })
    },
    [post, contentFieldId, content, scoreFieldId, score]
  )
  return {
    content, setContent,
    score, setScore,
    scores,
    submit,
    error: postState.error,
    posting: postState.posting
  }
}

const SectionRowEditForm = ({
  content, setContent,
  score, setScore,
  scores
}: ReturnType<typeof useSectionRowEditForm>) => {
  return (
    <Stack direction="column">
      <Stack direction="row" spacing={1}>
        <FormControl size="small" sx={{ flexGrow: 1 }}>
          <TextField
            value={content}
            onChange={(event) => setContent(event.target.value)}
            variant="outlined"
            fullWidth
            multiline
            size="small"
          />
				</FormControl>
        <FormControl size="small">
          <Select value={score} onChange={(event) => setScore(event.target.value)}>
            {scores.map((s, i) => <MenuItem value={s.value} key={`score-${i}`}>{s.value}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  )
}

type SectionRowEditDialogProps = {
  searchId: number
  kojinCode: string
  row: EvaluationSectionRow
  scores: EvaluationFormResult["scores"]
  onSubmit: (row: EvaluationSectionRow) => void
  onClose: () => void
}
export const SectionRowEditDialog = ({
  searchId,
  kojinCode,
  row: {
    contentFieldId,
    content,
    scoreFieldId,
    score
  },
  scores,
  onSubmit,
  onClose
}: SectionRowEditDialogProps) => {
  const form = useSectionRowEditForm({
    searchId,
    kojinCode,
    fields: {
      contentFieldId,
      scoreFieldId
    },
    storeData: {      
      content,
      score
    },
    scores,
    onSubmit
  })
  return (
    <FormDialog
      open
      fullWidth
      maxWidth="sm"
      title={`${contentFieldId}編集`}
      isLoading={form.posting}
      error={form.error}
      body={<SectionRowEditForm {...form}/>}
      submitLabel="保 存"
      onSubmit={(e) => {
        e.preventDefault()
        form.submit()
      }}
      onClose={onClose}
    />
  )
}